import { Asset, AssetCategory, AssetStatus } from '@/types/resources/asset';
import { ActivityType } from '@/types/common';
import dayjs from 'dayjs';

// Helper function to create activity records with proper typing
const createActivityRecord = (type: ActivityType = 'created') => ({
  timestamp: dayjs('2024-01-15').toDate(),
  type,
  performedBy: { id: 'USR001', displayName: 'Property Manager' }
});

// Realistic apartment society assets with proper categories and details
const apartmentAssets: Asset[] = [
  // ELEVATORS & LIFTS
  {
    id: 'AST001',
    name: 'Main Building Elevator - Block A',
    category: 'structural',
    subCategory: 'elevator',
    status: 'good',
    location: 'Block A, Ground Floor to 15th Floor',
    purchaseDate: dayjs('2018-06-15').toDate(),
    purchasePrice: { amount: 45000, currency: 'USD' },
    warranty: {
      startDate: dayjs('2018-06-15').toDate(),
      endDate: dayjs('2023-06-15').toDate(),
      details: '5-year comprehensive warranty including maintenance',
      provider: 'Otis Elevator Company'
    },
    specifications: {
      brand: 'Otis',
      model: 'Gen3',
      serialNumber: 'OTIS-G3-2018-001',
      dimensions: '2.4m x 2.1m x 3.2m',
      weight: 2800,
      color: 'Stainless Steel',
      capacity: '8 persons / 630 kg',
      speed: '1.6 m/s'
    },
    documents: [],
    activityRecords: [createActivityRecord()]
  },
  {
    id: 'AST002',
    name: 'Service Elevator - Block A',
    category: 'structural',
    subCategory: 'elevator',
    status: 'good',
    location: 'Block A, Service Area',
    purchaseDate: dayjs('2018-06-15').toDate(),
    purchasePrice: { amount: 38000, currency: 'USD' },
    warranty: {
      startDate: dayjs('2018-06-15').toDate(),
      endDate: dayjs('2023-06-15').toDate(),
      details: '5-year comprehensive warranty',
      provider: 'Otis Elevator Company'
    },
    specifications: {
      brand: 'Otis',
      model: 'Gen3 Service',
      serialNumber: 'OTIS-G3S-2018-002',
      dimensions: '3.0m x 2.4m x 3.2m',
      weight: 3200,
      color: 'Industrial Gray',
      capacity: '12 persons / 1000 kg',
      speed: '1.0 m/s'
    },
    documents: [],
    activityRecords: [createActivityRecord()]
  },
  {
    id: 'AST003',
    name: 'Main Building Elevator - Block B',
    category: 'structural',
    subCategory: 'elevator',
    status: 'good',
    location: 'Block B, Ground Floor to 12th Floor',
    purchaseDate: dayjs('2019-03-20').toDate(),
    purchasePrice: { amount: 42000, currency: 'USD' },
    warranty: {
      startDate: dayjs('2019-03-20').toDate(),
      endDate: dayjs('2024-03-20').toDate(),
      details: '5-year comprehensive warranty',
      provider: 'Schindler Group'
    },
    specifications: {
      brand: 'Schindler',
      model: '5500',
      serialNumber: 'SCH-5500-2019-003',
      dimensions: '2.4m x 2.1m x 3.2m',
      weight: 2750,
      color: 'Stainless Steel',
      capacity: '8 persons / 630 kg',
      speed: '1.6 m/s'
    },
    documents: [],
    activityRecords: [createActivityRecord()]
  },

  // WATER SYSTEMS
  {
    id: 'AST004',
    name: 'Main Water Storage Tank',
    category: 'plumbing',
    subCategory: 'water_tank',
    status: 'good',
    location: 'Roof Level, Block A',
    purchaseDate: dayjs('2017-08-10').toDate(),
    purchasePrice: { amount: 8500, currency: 'USD' },
    warranty: {
      startDate: dayjs('2017-08-10').toDate(),
      endDate: dayjs('2022-08-10').toDate(),
      details: '5-year warranty on tank structure',
      provider: 'Sintex Industries'
    },
    specifications: {
      brand: 'Sintex',
      model: 'XL-50000L',
      serialNumber: 'SIN-XL50K-2017-004',
      dimensions: '5.0m x 5.0m x 2.5m',
      weight: 1200,
      color: 'Blue',
      capacity: '50,000 liters',
      material: 'Food-grade HDPE'
    },
    documents: [],
    activityRecords: [createActivityRecord()]
  },
  {
    id: 'AST005',
    name: 'Booster Pump System',
    category: 'plumbing',
    subCategory: 'pump',
    status: 'good',
    location: 'Basement, Pump Room',
    purchaseDate: dayjs('2017-08-10').toDate(),
    purchasePrice: { amount: 3200, currency: 'USD' },
    warranty: {
      startDate: dayjs('2017-08-10').toDate(),
      endDate: dayjs('2022-08-10').toDate(),
      details: '5-year warranty on motor and pump',
      provider: 'Kirloskar Brothers'
    },
    specifications: {
      brand: 'Kirloskar',
      model: 'KBL-15HP',
      serialNumber: 'KIR-KBL15-2017-005',
      dimensions: '1.2m x 0.8m x 0.6m',
      weight: 180,
      color: 'Industrial Green',
      power: '15 HP',
      flowRate: '200 LPM',
      pressure: '4 bar'
    },
    documents: [],
    activityRecords: [createActivityRecord()]
  },

  // ELECTRICAL SYSTEMS
  {
    id: 'AST006',
    name: 'Main Electrical Distribution Board',
    category: 'electrical',
    subCategory: 'distribution_board',
    status: 'good',
    location: 'Basement, Electrical Room',
    purchaseDate: dayjs('2017-05-12').toDate(),
    purchasePrice: { amount: 6800, currency: 'USD' },
    warranty: {
      startDate: dayjs('2017-05-12').toDate(),
      endDate: dayjs('2022-05-12').toDate(),
      details: '5-year warranty on components',
      provider: 'Schneider Electric'
    },
    specifications: {
      brand: 'Schneider',
      model: 'Prisma Plus P',
      serialNumber: 'SCH-PP-2017-006',
      dimensions: '1.8m x 0.6m x 0.4m',
      weight: 250,
      color: 'Industrial Gray',
      capacity: '400A main switch',
      protection: 'RCCB, MCB, Surge protection'
    },
    documents: [],
    activityRecords: [createActivityRecord()]
  },
  {
    id: 'AST007',
    name: 'Backup Diesel Generator',
    category: 'electrical',
    subCategory: 'generator',
    status: 'good',
    location: 'Basement, Generator Room',
    purchaseDate: dayjs('2017-05-12').toDate(),
    purchasePrice: { amount: 25000, currency: 'USD' },
    warranty: {
      startDate: dayjs('2017-05-12').toDate(),
      endDate: dayjs('2022-05-12').toDate(),
      details: '5-year comprehensive warranty',
      provider: 'Cummins India'
    },
    specifications: {
      brand: 'Cummins',
      model: 'C1100D5',
      serialNumber: 'CUM-C1100-2017-007',
      dimensions: '3.2m x 1.5m x 1.8m',
      weight: 2800,
      color: 'Industrial Yellow',
      power: '1100 KVA',
      fuel: 'Diesel',
      fuelCapacity: '500 liters'
    },
    documents: [],
    activityRecords: [createActivityRecord()]
  },

  // SECURITY SYSTEMS
  {
    id: 'AST008',
    name: 'CCTV Surveillance System',
    category: 'security',
    subCategory: 'security_cameras',
    status: 'good',
    location: 'Entire Complex',
    purchaseDate: dayjs('2018-01-20').toDate(),
    purchasePrice: { amount: 12000, currency: 'USD' },
    warranty: {
      startDate: dayjs('2018-01-20').toDate(),
      endDate: dayjs('2023-01-20').toDate(),
      details: '5-year warranty on cameras and DVR',
      provider: 'Hikvision'
    },
    specifications: {
      brand: 'Hikvision',
      model: 'DS-2CD2T47G1-L',
      serialNumber: 'HIK-DS2CD-2018-008',
      dimensions: 'Various (IP Cameras)',
      weight: 150,
      color: 'Black',
      resolution: '4MP Ultra HD',
      nightVision: 'Yes (IR)',
      storage: '2TB DVR'
    },
    documents: [],
    activityRecords: [createActivityRecord()]
  },
  {
    id: 'AST009',
    name: 'Access Control System',
    category: 'security',
    subCategory: 'access_control',
    status: 'good',
    location: 'Main Gate & All Blocks',
    purchaseDate: dayjs('2018-01-20').toDate(),
    purchasePrice: { amount: 8500, currency: 'USD' },
    warranty: {
      startDate: dayjs('2018-01-20').toDate(),
      endDate: dayjs('2023-01-20').toDate(),
      details: '5-year warranty on hardware',
      provider: 'Honeywell'
    },
    specifications: {
      brand: 'Honeywell',
      model: 'Pro-Watch',
      serialNumber: 'HON-PW-2018-009',
      dimensions: 'Various (Card Readers)',
      weight: 200,
      color: 'Silver',
      type: 'RFID Card + Biometric',
      capacity: '1000 users'
    },
    documents: [],
    activityRecords: [createActivityRecord()]
  },

  // AMENITIES
  {
    id: 'AST010',
    name: 'Swimming Pool Filtration System',
    category: 'amenities',
    subCategory: 'pool_equipment',
    status: 'good',
    location: 'Pool Area, Ground Floor',
    purchaseDate: dayjs('2018-09-15').toDate(),
    purchasePrice: { amount: 5800, currency: 'USD' },
    warranty: {
      startDate: dayjs('2018-09-15').toDate(),
      endDate: dayjs('2023-09-15').toDate(),
      details: '5-year warranty on pump and filter',
      provider: 'Pentair'
    },
    specifications: {
      brand: 'Pentair',
      model: 'Clean & Clear Plus',
      serialNumber: 'PEN-CCP-2018-010',
      dimensions: '0.8m x 0.6m x 1.2m',
      weight: 120,
      color: 'White',
      flowRate: '150 GPM',
      filterSize: '420 sq ft',
      pumpPower: '2 HP'
    },
    documents: [],
    activityRecords: [createActivityRecord()]
  },
  {
    id: 'AST011',
    name: 'Gym Equipment Set',
    category: 'amenities',
    subCategory: 'gym_equipment',
    status: 'good',
    location: 'Fitness Center, Ground Floor',
    purchaseDate: dayjs('2018-09-15').toDate(),
    purchasePrice: { amount: 15000, currency: 'USD' },
    warranty: {
      startDate: dayjs('2018-09-15').toDate(),
      endDate: dayjs('2023-09-15').toDate(),
      details: '5-year warranty on equipment',
      provider: 'Life Fitness'
    },
    specifications: {
      brand: 'Life Fitness',
      model: 'Signature Series',
      serialNumber: 'LIF-SS-2018-011',
      dimensions: 'Various (Multiple pieces)',
      weight: 2500,
      color: 'Black & Chrome',
      includes: 'Treadmills, Ellipticals, Weight Machines',
      capacity: '20+ users simultaneously'
    },
    documents: [],
    activityRecords: [createActivityRecord()]
  },

  // WASTE MANAGEMENT
  {
    id: 'AST012',
    name: 'Waste Compactor System',
    category: 'maintenance',
    subCategory: 'waste_management',
    status: 'good',
    location: 'Basement, Waste Room',
    purchaseDate: dayjs('2019-02-10').toDate(),
    purchasePrice: { amount: 4200, currency: 'USD' },
    warranty: {
      startDate: dayjs('2019-02-10').toDate(),
      endDate: dayjs('2024-02-10').toDate(),
      details: '5-year warranty on motor and compactor',
      provider: 'Waste Management Solutions'
    },
    specifications: {
      brand: 'WMS',
      model: 'WC-500L',
      serialNumber: 'WMS-WC500-2019-012',
      dimensions: '2.0m x 1.5m x 2.5m',
      weight: 800,
      color: 'Industrial Green',
      capacity: '500 liters',
      compression: '5:1 ratio',
      power: '2 HP motor'
    },
    documents: [],
    activityRecords: [createActivityRecord()]
  },

  // FIRE SAFETY
  {
    id: 'AST013',
    name: 'Fire Alarm Control Panel',
    category: 'security',
    subCategory: 'fire_safety',
    status: 'good',
    location: 'Security Room, Ground Floor',
    purchaseDate: dayjs('2017-12-01').toDate(),
    purchasePrice: { amount: 3800, currency: 'USD' },
    warranty: {
      startDate: dayjs('2017-12-01').toDate(),
      endDate: dayjs('2022-12-01').toDate(),
      details: '5-year warranty on panel and sensors',
      provider: 'Honeywell'
    },
    specifications: {
      brand: 'Honeywell',
      model: 'Fire-Lite ES-50X',
      serialNumber: 'HON-FLES50-2017-013',
      dimensions: '0.6m x 0.4m x 0.2m',
      weight: 45,
      color: 'Red',
      zones: '50 zones',
      detectors: 'Smoke, Heat, Manual call points',
      backup: '24-hour battery backup'
    },
    documents: [],
    activityRecords: [createActivityRecord()]
  },
  {
    id: 'AST014',
    name: 'Fire Extinguishers (Complete Set)',
    category: 'security',
    subCategory: 'fire_extinguishers',
    status: 'good',
    location: 'All Floors & Common Areas',
    purchaseDate: dayjs('2017-12-01').toDate(),
    purchasePrice: { amount: 2800, currency: 'USD' },
    warranty: {
      startDate: dayjs('2017-12-01').toDate(),
      endDate: dayjs('2022-12-01').toDate(),
      details: '5-year warranty on extinguishers',
      provider: 'Kidde'
    },
    specifications: {
      brand: 'Kidde',
      model: 'Pro 210',
      serialNumber: 'KID-PRO210-2017-014',
      dimensions: 'Various (Multiple units)',
      weight: 300,
      color: 'Red',
      type: 'ABC Dry Chemical',
      capacity: '50 units (5kg each)',
      coverage: 'All floors and common areas'
    },
    documents: [],
    activityRecords: [createActivityRecord()]
  },

  // HVAC SYSTEMS
  {
    id: 'AST015',
    name: 'Central Air Conditioning System',
    category: 'electrical',
    subCategory: 'hvac',
    status: 'good',
    location: 'Common Areas & Lobby',
    purchaseDate: dayjs('2018-04-15').toDate(),
    purchasePrice: { amount: 18000, currency: 'USD' },
    warranty: {
      startDate: dayjs('2018-04-15').toDate(),
      endDate: dayjs('2023-04-15').toDate(),
      details: '5-year comprehensive warranty',
      provider: 'Carrier'
    },
    specifications: {
      brand: 'Carrier',
      model: '48TC',
      serialNumber: 'CAR-48TC-2018-015',
      dimensions: '3.0m x 2.5m x 2.0m',
      weight: 1200,
      color: 'White',
      cooling: '10 tons',
      heating: 'Electric heat',
      efficiency: '16 SEER'
    },
    documents: [],
    activityRecords: [createActivityRecord()]
  }
];

// Generate additional assets to reach 235 total
const generateAdditionalAssets = (): Asset[] => {
  const additionalAssets: Asset[] = [];
  
  // Common apartment assets
  const commonAssets = [
    { name: 'Water Heater', category: 'plumbing', subCategory: 'water_heater', basePrice: 800 },
    { name: 'Intercom System', category: 'security', subCategory: 'intercom', basePrice: 1200 },
    { name: 'Garden Sprinkler System', category: 'maintenance', subCategory: 'irrigation', basePrice: 2500 },
    { name: 'Parking Gate Barrier', category: 'security', subCategory: 'parking_system', basePrice: 1800 },
    { name: 'Solar Water Heater', category: 'electrical', subCategory: 'solar', basePrice: 3500 },
    { name: 'Garbage Chute System', category: 'maintenance', subCategory: 'waste_management', basePrice: 4200 },
    { name: 'Playground Equipment', category: 'amenities', subCategory: 'playground', basePrice: 15000 },
    { name: 'BBQ Grill Station', category: 'amenities', subCategory: 'outdoor_cooking', basePrice: 2800 },
    { name: 'Bicycle Rack', category: 'amenities', subCategory: 'parking', basePrice: 800 },
    { name: 'Mailbox System', category: 'amenities', subCategory: 'mail', basePrice: 1200 }
  ];

  for (let i = 16; i <= 235; i++) {
    const assetType = commonAssets[i % commonAssets.length];
    const asset = {
      id: `AST${String(i).padStart(3, '0')}`,
      name: `${assetType.name} - Unit ${Math.floor(Math.random() * 100) + 1}`,
      category: assetType.category as AssetCategory,
      subCategory: assetType.subCategory,
      status: Math.random() > 0.1 ? 'good' : 'maintenance' as AssetStatus,
      location: `Block ${String.fromCharCode(65 + (i % 5))}, Floor ${Math.floor(Math.random() * 15) + 1}`,
      purchaseDate: dayjs().subtract(Math.floor(Math.random() * 5 * 365), 'days').toDate(),
      purchasePrice: {
        amount: assetType.basePrice + Math.floor(Math.random() * 1000),
        currency: 'USD'
      },
      warranty: {
        startDate: dayjs().subtract(Math.floor(Math.random() * 5 * 365), 'days').toDate(),
        endDate: dayjs().add(Math.floor(Math.random() * 2 * 365), 'days').toDate(),
        details: 'Standard warranty',
        provider: 'Various Manufacturers'
      },
      specifications: {
        brand: 'Various',
        model: 'Standard',
        serialNumber: `VAR-${i.toString().padStart(6, '0')}`,
        dimensions: 'Standard',
        weight: Math.floor(Math.random() * 100) + 10,
        color: 'Standard'
      },
      documents: [],
      activityRecords: [createActivityRecord()]
    };
    additionalAssets.push(asset);
  }

  return additionalAssets;
};

export const assets: Asset[] = [
  ...apartmentAssets,
  ...generateAdditionalAssets()
];

 